// src/hooks/useDiscount.js
import { useState, useEffect, useCallback } from "react";
import discountService from "@/services/discountService";

const safeToast = {
  success: (m) => {
    try {
      console.log("SUCCESS:", m);
    } catch (e) {
      console.log("SUCCESS:", m);
    }
  },
  error: (m) => {
    try {
      // window.toast?.error?.(m);
      console.error("ERROR:", m);
    } catch (e) {
      console.error("ERROR:", m);
    }
  },
};


// Helper to map appliesTo from API to UI format
const mapAppliesToUI = (appliesTo) => {
  const val = (appliesTo || "").toLowerCase();
  if (val === "bill") return "ALL";
  if (val === "item") return "ITEM";
  if (val === "category") return "CATEGORY";
  return "ALL"; // default
};

const mapDiscountTypeToUI = (d) => {
  switch (d.discountType) {
    case "fixed": return "FLAT";
    case "bogo":
      return d.buyQuantity && d.getQuantity && d.buyQuantity !== 1 ? "BUY_X_GET_Y" : "BOGO";
    case "complimentary": return "COMPLIMENTARY";
    case "bundle": return "BUNDLE";
    case "tiered": return "TIERED";
    case "percentage":
    default: return "PERCENTAGE";
  }
};

const mapDiscountFromApi = (d = {}) => ({
  ...d,
  type: mapDiscountTypeToUI(d),
  value: d.discountValue ?? "",
  minOrderAmount: d.minBillAmount ?? "",
  maxDiscountAmount: d.maxDiscountAmount ?? null,
  applicableTo: mapAppliesToUI(d.appliesTo), // Map to uppercase for UI
  applicableCategories: d.idCategories || [],
  applicableItems: d.idMenuItems || [],
  applicableOrderTypes: d.orderTypes || [],
  orderTypes: d.orderTypes || [], // Ensure orderTypes is always an array
  // New fields for item and category names (from API join)
  menuItems: d.menuItems || [], // Array of { id, name }
  categories: d.categories || [], // Array of { id, name }
  startDate: d.validFrom || "",
  endDate: d.validUntil || "",
  startTime: d.startTime ? d.startTime.slice(0, 5) : "",
  endTime: d.endTime ? d.endTime.slice(0, 5) : "",
  daysOfWeek: Array.isArray(d.daysOfWeek)
    ? d.daysOfWeek.map((x) => (typeof x === "string" ? x.toUpperCase() : x))
    : [],
  isActive: d.isActive,
  branchId: d.branchId ?? "",
  branchName: d.branchName ?? "",
});

export const useDiscount = () => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchDiscounts = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      const res = await discountService.getAll(filters);
      const raw =
        Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : Array.isArray(res?.data?.data) ? res.data.data : [];
      setDiscounts(raw.map(mapDiscountFromApi));
    } catch (err) {
      console.error("Failed to fetch discounts", err);
      safeToast.error("Failed to load discounts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDiscounts();
  }, [fetchDiscounts]);

  const createDiscount = useCallback(async (apiPayload) => {
    try {
      const res = await discountService.create(apiPayload);
      // Accept res.data (axios) or res (fetch wrapper)
      const raw = res?.data ?? res ?? apiPayload;
      const created = mapDiscountFromApi(raw);
      setDiscounts((prev) => [...prev, created]);
      safeToast.success("Discount created");
      return created;
    } catch (err) {
      console.error("Create discount failed", err);
      const msg = err?.response?.data?.message || err?.message || "Failed to create discount";
      safeToast.error(msg);
      throw err;
    }
  }, []);

  const updateDiscount = useCallback(async (id, apiPayload) => {
    try {
      const res = await discountService.update(id, apiPayload);
      const raw = res?.data ?? res ?? { ...apiPayload, id };
      const updated = mapDiscountFromApi(raw);
      setDiscounts((prev) => prev.map((d) => (d.id === id ? updated : d)));
      safeToast.success("Discount updated");
      return updated;
    } catch (err) {
      console.error("Update discount failed", err);
      const msg = err?.response?.data?.message || err?.message || "Failed to update discount";
      safeToast.error(msg);
      throw err;
    }
  }, []);

 const deleteDiscount = useCallback(
  async (id, { preferRefetch = false } = {}) => {
    try {
      const res = await discountService.remove(id);

      // Verbose logging
      console.log("[deleteDiscount] raw response:", res);

      // Normalize
      const status = res?.status ?? res?.statusCode ?? null;
      const data = res?.data ?? res ?? null;

      console.log("[deleteDiscount] normalized status:", status, "data:", data);

      if (data && typeof data === "object") {
        
        const returnedIsActive = "isActive" in data ? data.isActive : null;
        const returnedMessage = data.message || data.msg || "";

        if (returnedIsActive !== null) {
      
          setDiscounts((prev) =>
            prev.map((d) => (d.id === id ? { ...d, isActive: returnedIsActive } : d))
          );
          safeToast.success(data.message ?? "Discount updated");
          return res;
        }
        if (data.success === true) {
          if (preferRefetch) {
            await fetchDiscounts();
          } else {
         
            setDiscounts((prev) =>
              prev.map((d) => (d.id === id ? { ...d, isActive: false } : d))
            );
          }
          safeToast.success(data.message ?? "Discount deleted (soft)");
          return res;
        }
      }

      if (typeof data === "string") {
        const str = data.toLowerCase();
        if (/deleted|soft/i.test(str)) {

          setDiscounts((prev) => prev.map((d) => (d.id === id ? { ...d, isActive: false } : d)));
          safeToast.success("Discount marked inactive (soft-deleted)");
       
          if (preferRefetch) await fetchDiscounts();
          return res;
        }

 
        const isStatus2xx = typeof status === "number" && status >= 200 && status < 300;
        if (isStatus2xx) {
          if (preferRefetch) {
            await fetchDiscounts();
          } else {
          
            await fetchDiscounts();
          }
          safeToast.success("Discount deletion acknowledged by server");
          return res;
        }
      }

      console.error("[deleteDiscount] non-success delete response:", data);
      const msg = (data && (data.message || JSON.stringify(data))) || `Unexpected status ${status ?? "unknown"}`;
      throw new Error(msg);
    } catch (err) {
      console.error("Delete discount failed (hook):", err);
      let backendMsg = "Failed to delete discount";
      if (err?.response?.data) {
        backendMsg = err.response.data.message || JSON.stringify(err.response.data);
      } else if (err?.message) {
        backendMsg = err.message;
      } else if (err?.request) {
        backendMsg = "No response from server (network error)";
      }
      safeToast.error(backendMsg);
      throw err;
    }
  },
  [fetchDiscounts]
);
 


  const deactivateDiscount = useCallback(async (id) => {
    try {
      await discountService.deactivate(id);
      setDiscounts((prev) => prev.map((d) => (d.id === id ? { ...d, isActive: false } : d)));
      safeToast.success("Discount deactivated");
    } catch (err) {
      console.error("Deactivate discount failed", err);
      safeToast.error(err?.response?.data?.message || "Failed to deactivate discount");
      throw err;
    }
  }, []);


  const getDiscountRawById = useCallback(async (id) => {
    const res = await discountService.getById(id);
    return res?.data ?? res;
  }, []);

  const getDiscountById = useCallback(
    async (id) => {
      const raw = await getDiscountRawById(id);
      return mapDiscountFromApi(raw);
    },
    [getDiscountRawById]
  );

  return {
    discounts,
    loading,
    fetchDiscounts,
    createDiscount,
    updateDiscount,
    deleteDiscount,
    deactivateDiscount,
    getDiscountById,
    getDiscountRawById,
    setDiscounts,
  };
};

export default useDiscount;
